import { InteractionRequiredAuthError } from "@azure/msal-browser";
import axios, { AxiosInstance } from "axios";
import { authPersistence } from "../utils/authPersistence";
import { msalInstance } from "./authConfig";

// Create an Axios instance
const apiClient = axios.create({
	baseURL: import.meta.env.VITE_API_BACKEND_EINSTEIN_URL,
	headers: {
		"Content-Type": "application/json",
	},
});

export const abacusApiClient = axios.create({
	baseURL: import.meta.env.VITE_BACKEND_ABACUS_URL,
	headers: {
		"Content-Type": "application/json",
	},
});

// Add a request interceptor for all clients
const addAuthInterceptor = (client: AxiosInstance) => {
	client.interceptors.request.use(
		(config) => {
			const token = apiClient.defaults.headers.common["Authorization"];
			if (token) {
				config.headers.Authorization = token;
			}
			return config;
		},
		(error: unknown) => {
			return Promise.reject(error);
		},
	);
};

addAuthInterceptor(apiClient);
addAuthInterceptor(abacusApiClient);

// Add a response interceptor for both clients
const addResponseInterceptor = (client: AxiosInstance) => {
	client.interceptors.response.use(
		(response) => {
			return response;
		},
		async (error) => {
			const originalRequest = error.config;

			// Handle 401 Unauthorized errors
			if (error.response?.status === 401 && !originalRequest._retry) {
				originalRequest._retry = true;

				try {
					const account = msalInstance.getActiveAccount();
					if (account) {
						// Try to acquire token silently with force refresh
						const tokenResponse = await msalInstance.acquireTokenSilent({
							scopes: ["user.read", "profile", "openid", "email"],
							account,
							forceRefresh: true,
						});

						// Update the authorization header with new token
						const newToken = `Bearer ${tokenResponse.accessToken}`;
						apiClient.defaults.headers.common["Authorization"] = newToken;
						abacusApiClient.defaults.headers.common["Authorization"] = newToken;
						originalRequest.headers.Authorization = newToken;

						// Retry the original request with new token
						return client(originalRequest);
					}
				} catch (tokenError) {
					// If silent refresh requires interaction, attempt SSO silent using saved login hint
					if (tokenError instanceof InteractionRequiredAuthError) {
						try {
							const saved = authPersistence.getSavedAccount();
							const ssoResp = await msalInstance.ssoSilent({
								scopes: ["user.read", "profile", "openid", "email"],
								loginHint: saved?.username,
							});
							const newToken = `Bearer ${ssoResp.accessToken}`;
							apiClient.defaults.headers.common["Authorization"] = newToken;
							abacusApiClient.defaults.headers.common["Authorization"] = newToken;
							originalRequest.headers.Authorization = newToken;
							return client(originalRequest);
						} catch (ssoErr) {
							// Fall back to interactive login redirect, preserving path
							console.error("[Auth] Silent SSO failed, redirecting to login");
							const currentUrl =
								window.location.pathname +
								window.location.search +
								window.location.hash;
							sessionStorage.setItem("post_login_redirect", currentUrl);
							await msalInstance.loginRedirect({
								scopes: ["user.read", "profile", "openid", "email"],
								prompt: "select_account",
								loginHint: authPersistence.getSavedAccount()?.username,
							});
						}
					}
				}
			}

			// Handle network and server errors
			const isNetworkError = error.code === "ERR_NETWORK";
			const isServerError = error.response && error.response.status === 500;
			const isOnServerDownPage = window.location.pathname === "/server-down";

			if ((isNetworkError || isServerError) && !isOnServerDownPage) {
				// console.log('Server is down');
				// window.location.href = '/server-down';
			}

			return Promise.reject(error);
		},
	);
};

addResponseInterceptor(apiClient);
addResponseInterceptor(abacusApiClient);

export default apiClient;
